import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { FInput, FSelect, FLabel } from "../pages/engagement/FormControls";
import { MED_FORMS, MED_ROUTES, presentationLabel } from "../lib/medVocab";
import { Search, Pill, Plus, RotateCcw } from "lucide-react";

const OTHER = "__other";

/** Before saving a chart record, add a new medication or strength to the library when the user asked for it. */
export async function resolveLibrary(form) {
  const { _save_to_library: save, _custom: custom, ...rest } = form;
  if (rest.presentation_id === OTHER) rest.presentation_id = "";
  if (!save || !custom || !rest.medication.trim()) return rest;
  const r = await api.post("/medication-library/quick-add", { name: rest.medication, strength: rest.strength, form: rest.form, route: rest.route });
  const p = r.data.presentation;
  return { ...rest, medication: r.data.medication.name, catalog_id: r.data.medication.id, presentation_id: p?.id || "",
    ...(p ? { strength: p.strength, form: p.form || rest.form, route: p.route || rest.route } : {}) };
}

function ReviewBadge({ med }) {
  if (med.review_status !== "pending_review") return null;
  return <span className="rounded-full bg-[#FFF3E0] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#8D5A00]">Pending clinical review</span>;
}

function SearchBox({ testId, onPick, onNew }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get("/medication-library/search", { params: { q, limit: 15 } }).then((r) => setRows(r.data)).catch(() => setRows([])), 180);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <input data-testid={testId} value={q} autoComplete="off" placeholder="Search the Medication Library"
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          className="h-11 w-full rounded-lg border border-[#C9B8A6] bg-[#FDFCF8] pl-9 pr-3 text-sm text-[#3E2723] outline-none focus:ring-2 focus:ring-[#8D6E63]" />
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
      </div>
      {open && (
        <div data-testid={`${testId}-results`} className="absolute z-50 mt-1 max-h-80 w-full overflow-y-auto rounded-xl border border-[#EFEBE0] bg-white shadow-lg">
          {rows.map((m) => (
            <button key={m.id} type="button" data-testid={`${testId}-option-${m.name_key?.replace(/\W+/g, "-")}`} onClick={() => { onPick(m); setOpen(false); setQ(""); }}
              className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-[#FDFCF8]">
              <span className="mt-0.5 rounded-full bg-[#F5F2EB] p-1 text-[#7B6157]"><Pill size={11} /></span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-[#3E2723]">{m.name}
                  {m.brand && <span className="text-[10px] font-normal text-[#7B6157]">({m.brand})</span>}
                  {m.schedule && <span className="rounded-full bg-[#FFEBEE] px-1.5 py-0.5 text-[9px] font-bold text-[#C62828]">DEA {m.schedule}</span>}
                  <ReviewBadge med={m} />
                </span>
                <span className="block text-[11px] text-[#7B6157]">{m.class}{m.class ? " · " : ""}{(m.presentations || []).length} strength{(m.presentations || []).length === 1 ? "" : "s"}</span>
              </span>
            </button>
          ))}
          <button type="button" data-testid={`${testId}-add-new`} onClick={() => { onNew(q); setOpen(false); }}
            className="flex w-full items-center gap-2 border-t border-[#F1EEE7] px-3 py-2.5 text-left text-sm font-semibold text-[#4E342E] hover:bg-[#F5F2EB]">
            <Plus size={13} /> {q.trim() ? `Add "${q.trim()}" as a new medication` : "Add a medication that is not in the library"}
          </button>
        </div>
      )}
    </div>
  );
}

function CustomFields({ value, onChange, testId, showName }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {showName && <div className="col-span-2"><FLabel>Medication name (no strength)</FLabel>
        <FInput data-testid={`${testId}-new-name`} value={value.medication} onChange={(e) => onChange({ medication: e.target.value })} placeholder="Sertraline" /></div>}
      <div><FLabel>Strength</FLabel><FInput data-testid={`${testId}-new-strength`} value={value.strength} onChange={(e) => onChange({ strength: e.target.value })} placeholder="50 mg" /></div>
      <div><FLabel>Form</FLabel><FSelect data-testid={`${testId}-new-form`} value={value.form} placeholder="Select form" onChange={(e) => onChange({ form: e.target.value })}>{MED_FORMS.map((x) => <option key={x} value={x}>{x}</option>)}</FSelect></div>
      <div className="col-span-2"><FLabel>Route</FLabel><FSelect data-testid={`${testId}-new-route`} value={value.route} placeholder="Select route" onChange={(e) => onChange({ route: e.target.value })}>{MED_ROUTES.map((x) => <option key={x} value={x}>{x}</option>)}</FSelect></div>
      <label className="col-span-2 flex items-center gap-2 text-xs text-[#5D4037]">
        <input type="checkbox" data-testid={`${testId}-save-to-library`} checked={!!value._save_to_library} onChange={(e) => onChange({ _save_to_library: e.target.checked })} />
        Save to the Medication Library so it is available next time
      </label>
    </div>
  );
}

/** Pick a medication, then its strength. Or add a new medication or strength that can save back to the library. */
export function MedicationPicker({ value, onChange, testId = "med-picker" }) {
  const [med, setMed] = useState(null);
  const [mode, setMode] = useState(value.catalog_id || !value.medication ? "library" : "legacy");
  useEffect(() => {
    if (value.catalog_id && (!med || med.id !== value.catalog_id)) api.get(`/medication-library/${value.catalog_id}`).then((r) => setMed(r.data)).catch(() => {});
  }, [value.catalog_id]); // eslint-disable-line
  const pickMed = (m) => {
    setMed(m); setMode("library");
    const p = (m.presentations || [])[0];
    onChange({ medication: m.name, catalog_id: m.id, presentation_id: p?.id || "", strength: p?.strength || "", form: p?.form || "", route: p?.route || "",
      dea_schedule: m.schedule || "", _custom: false, _save_to_library: false });
  };
  const pickPresentation = (id) => {
    if (id === OTHER) return onChange({ presentation_id: OTHER, strength: "", _custom: true, _save_to_library: true });
    const p = (med?.presentations || []).find((x) => x.id === id);
    if (p) onChange({ presentation_id: p.id, strength: p.strength, form: p.form, route: p.route, _custom: false, _save_to_library: false });
  };
  const reset = () => { setMed(null); setMode("library"); onChange({ medication: "", catalog_id: "", presentation_id: "", strength: "", _custom: false, _save_to_library: false }); };

  if (mode === "new") return (
    <div className="space-y-3 rounded-xl border border-[#EFEBE0] bg-[#F9F5EE] p-3" data-testid={`${testId}-new`}>
      <CustomFields value={value} onChange={onChange} testId={testId} showName />
      <button type="button" onClick={reset} className="flex items-center gap-1 text-xs font-semibold text-[#6D4C41]"><RotateCcw size={12} /> Back to library search</button>
    </div>
  );
  if (med && mode === "library") return (
    <div className="space-y-3 rounded-xl border border-[#EFEBE0] bg-[#F9F5EE] p-3" data-testid={`${testId}-selected`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-1.5 font-heading font-bold text-[#3E2723]">{med.name}{med.brand && <span className="text-xs font-normal text-[#7B6157]">({med.brand})</span>}<ReviewBadge med={med} /></div>
          <div className="text-[11px] text-[#7B6157]">{med.class}{med.schedule ? ` · DEA Schedule ${med.schedule}` : ""}</div>
        </div>
        <button type="button" data-testid={`${testId}-change`} onClick={reset} className="text-xs font-semibold text-[#6D4C41] underline">Change</button>
      </div>
      <div><FLabel>Strength</FLabel>
        <FSelect data-testid={`${testId}-strength`} value={value._custom ? OTHER : value.presentation_id || ""} placeholder="Select strength" onChange={(e) => pickPresentation(e.target.value)}>
          {(med.presentations || []).map((p) => <option key={p.id} value={p.id}>{presentationLabel(p)}</option>)}
          <option value={OTHER}>Other strength, not listed</option>
        </FSelect>
      </div>
      {value._custom && <CustomFields value={value} onChange={onChange} testId={testId} />}
    </div>
  );
  return (
    <div className="space-y-2">
      <SearchBox testId={testId} onPick={pickMed} onNew={(q) => { setMode("new"); onChange({ medication: q.trim(), catalog_id: "", presentation_id: "", strength: "", _custom: true, _save_to_library: true }); }} />
      {mode === "legacy" && value.medication && (
        <p className="text-[11px] text-[#7B6157]" data-testid={`${testId}-legacy`}>Current entry: <strong>{value.medication} {value.strength || value.dosage}</strong>. Search above to link it to the library, or leave it as is.</p>
      )}
    </div>
  );
}
